'use client';

import { useEffect, useRef } from 'react';

interface ColorfulWavesProps {
  className?: string;
  opacity?: number;
  speed?: number;
}

export default function ColorfulWaves({
  className = '',
  opacity = 0.6,
  speed = 1
}: ColorfulWavesProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const raf = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = 0, h = 0, t = 0;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      t += 0.008 * speed;

      // Multiple colorful waves with different colors
      const waves = [
        {
          amp: 40,
          freq: 0.004,
          speed: 1.2,
          alpha: opacity * 0.3,
          yOff: 0.2,
          hue: (t * 20) % 360, // Rotating red/orange/gold
        },
        {
          amp: 35,
          freq: 0.006,
          speed: 0.9,
          alpha: opacity * 0.25,
          yOff: 0.35,
          hue: (t * 20 + 60) % 360, // Yellow/green
        },
        {
          amp: 50,
          freq: 0.003,
          speed: 1.5,
          alpha: opacity * 0.2,
          yOff: 0.5,
          hue: (t * 20 + 180) % 360, // Cyan/blue
        },
        {
          amp: 30,
          freq: 0.008,
          speed: 1.8,
          alpha: opacity * 0.2,
          yOff: 0.65,
          hue: (t * 20 + 270) % 360, // Purple/magenta
        },
        {
          amp: 45,
          freq: 0.005,
          speed: 1.1,
          alpha: opacity * 0.15,
          yOff: 0.8,
          hue: (t * 20 + 300) % 360, // Pink/red
        },
      ];

      for (const wave of waves) {
        const baseY = h * wave.yOff;
        ctx.beginPath();
        ctx.moveTo(0, h);

        for (let x = 0; x <= w; x += 3) {
          const y = baseY +
            Math.sin(x * wave.freq + t * wave.speed) * wave.amp +
            Math.sin(x * wave.freq * 0.5 + t * wave.speed * 0.8) * wave.amp * 0.5 +
            Math.cos(x * wave.freq * 0.3 + t * wave.speed * 1.2) * wave.amp * 0.3;
          ctx.lineTo(x, y);
        }

        ctx.lineTo(w, h);
        ctx.closePath();

        // Create gradient with HSL colors
        const grad = ctx.createLinearGradient(0, baseY - wave.amp * 2, 0, h);
        grad.addColorStop(0, `hsla(${wave.hue}, 70%, 60%, ${wave.alpha})`);
        grad.addColorStop(0.5, `hsla(${(wave.hue + 30) % 360}, 65%, 55%, ${wave.alpha * 0.6})`);
        grad.addColorStop(1, `hsla(${wave.hue}, 60%, 50%, 0)`);
        ctx.fillStyle = grad;
        ctx.fill();

        // Add glow effect on top edge
        ctx.strokeStyle = `hsla(${wave.hue}, 80%, 70%, ${wave.alpha * 0.5})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        for (let x = 0; x <= w; x += 3) {
          const y = baseY +
            Math.sin(x * wave.freq + t * wave.speed) * wave.amp +
            Math.sin(x * wave.freq * 0.5 + t * wave.speed * 0.8) * wave.amp * 0.5 +
            Math.cos(x * wave.freq * 0.3 + t * wave.speed * 1.2) * wave.amp * 0.3;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }

      raf.current = requestAnimationFrame(draw);
    };

    resize();
    draw();

    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(raf.current);
      window.removeEventListener('resize', resize);
    };
  }, [opacity, speed]);

  return (
    <canvas data-component-id="src/components/effects/ColorfulWaves.tsx:138:4" data-component-path="src/components/effects/ColorfulWaves.tsx" data-component-line="138" data-component-end-line="145" data-component-file="ColorfulWaves.tsx" data-component-name="canvas" data-component-content="%7B%22elementName%22%3A%22canvas%22%7D"
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
      style={{
        filter: 'blur(20px)',
        mixBlendMode: 'screen'
      }}
    />
  );
}
